import styled, { css } from "styled-components";
import { Outlet, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import { LuUserRound } from "react-icons/lu";
import TonginLogoIcon from "./icon/tonginLogo";
import CalendarModal from "./home/calendarModal";
import {
  filterOverPastContractList,
  getContractList,
} from "../API/AppRepository";
import { mobile, tabletP, tabletL, pc, paddingX } from "../styles/responsive";

const BRAND = "#ff7f3b";
const GREEN = "#6ad959";

/* Header heights per breakpoint (header + matching content offset) */
const H = { pc: 84, tabletL: 72, tabletP: 64, mobile: 56 };

// react-icons v5의 IconType(=> ReactNode)이 @types/react 18.2 JSX와 충돌하므로
// 렌더 가능한 컴포넌트 타입으로 캐스팅해 사용한다.
const asIcon = (C: unknown) => C as ComponentType<{ className?: string }>;
const ProfileIcon = asIcon(LuUserRound);

/* ===================== shared layout primitives ==================== */

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 999;
  display: flex;
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #f0f0f0;
  box-shadow: 0 2px 14px rgba(20, 20, 20, 0.05);

  /* 좌우 패딩: 콘텐츠와 동일 단일 소스 */
  ${paddingX}

  ${pc} {
    height: ${H.pc}px;
    gap: 24px;
  }
  ${tabletL} {
    height: ${H.tabletL}px;
    gap: 16px;
  }
  ${tabletP} {
    height: ${H.tabletP}px;
    gap: 14px;
  }
  ${mobile} {
    height: ${H.mobile}px;
  }
`;

/* --------------------- 로고 (PC / 태블릿) -------------------------- */

const Logo = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  ${pc} {
    width: 48px;
    height: 48px;
  }
  ${tabletL} {
    width: 42px;
    height: 42px;
  }
  ${tabletP} {
    width: 40px;
    height: 40px;
  }
  ${mobile} {
    display: none;
  }
`;

/* ------------------ 모바일 햄버거 + nav 드로어 -------------------- */

const NavMobile = styled.div`
  display: none;
  flex: 0 0 auto;
  align-items: center;

  ${mobile} {
    display: flex;
  }
`;

const Hamburger = styled.button`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 38px;
  height: 38px;
  border: none;
  background: transparent;
  cursor: pointer;

  span {
    display: block;
    width: 22px;
    height: 2px;
    margin: 0 auto;
    border-radius: 2px;
    background: #2a2f36;
  }
`;

const Drawer = styled.div`
  position: absolute;
  top: ${H.mobile}px;
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 16px 16px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 14px 26px rgba(0, 0, 0, 0.1);
  z-index: 9999;
`;

const DrawerLink = styled.div<{ $active?: boolean; $accent?: string }>`
  display: flex;
  align-items: center;
  height: 52px;
  padding: 0 8px;
  font-size: 18px;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  color: ${(p) => (p.$active ? p.$accent : "#3a3f45")};
  border-bottom: 1px solid #f4f4f4;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

/* ----------------------- 메뉴탭 (PC / 태블릿) --------------------- */

const Nav = styled.nav`
  display: flex;
  align-items: stretch;
  height: 100%;
  flex: 1 1 auto;
  min-width: 0;
  justify-content: center;

  ${pc} {
    gap: 6px;
  }
  ${tabletL} {
    gap: 4px;
  }
  ${tabletP} {
    gap: 2px;
  }
  ${mobile} {
    display: none;
  }
`;

const Tab = styled.div<{ $active?: boolean; $accent?: string }>`
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  color: ${(p) => (p.$active ? p.$accent : "#6b6f76")};
  transition: color 0.15s ease;

  &:hover {
    color: ${(p) => p.$accent};
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    height: 3px;
    border-radius: 3px;
    background: ${(p) => p.$accent};
    width: ${(p) => (p.$active ? "60%" : "0")};
    transition: width 0.2s ease;
  }

  ${pc} {
    padding: 0 24px;
    font-size: 24px;
  }
  ${tabletL} {
    padding: 0 18px;
    font-size: 21px;
  }
  ${tabletP} {
    padding: 0 16px;
    font-size: 19px;
  }
`;

/* --------------------------- user area ----------------------------- */

const UserArea = styled.div`
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const UserButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  cursor: pointer;
  background: #f4f6f8;
  transition: background 0.15s ease;
  font-family: inherit;
  color: ${BRAND};

  &:hover {
    background: #eaeef2;
  }

  /* PC: 아이콘 + 이름 pill */
  ${pc} {
    height: 50px;
    padding: 0 18px 0 12px;
    border-radius: 999px;
  }
  /* 태블릿/모바일: 아이콘만, 정사각 라운드 */
  ${tabletL} {
    width: 46px;
    height: 46px;
    padding: 0;
    justify-content: center;
    border-radius: 14px;
  }
  ${tabletP} {
    width: 44px;
    height: 44px;
    padding: 0;
    justify-content: center;
    border-radius: 13px;
  }
  ${mobile} {
    width: 38px;
    height: 38px;
    padding: 0;
    justify-content: center;
    border-radius: 11px;
  }
`;

const IconBox = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  color: ${BRAND};

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  ${pc} {
    width: 28px;
    height: 28px;
  }
  ${tabletL} {
    width: 26px;
    height: 26px;
  }
  ${tabletP} {
    width: 25px;
    height: 25px;
  }
  ${mobile} {
    width: 22px;
    height: 22px;
  }
`;

const UserName = styled.span`
  display: none;
  font-weight: 600;
  color: #2a2f36;
  white-space: nowrap;

  ${pc} {
    display: inline;
    font-size: 16px;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: ${BRAND};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  border-radius: 999px;
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* --------------------------- dropdown ------------------------------ */

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 210px;
  padding: 8px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.14);

  ${mobile} {
    top: calc(100% + 8px);
    min-width: 200px;
  }
`;

const NameLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 40px;
  padding: 0 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 15px;
  font-weight: 600;
  color: #2a2f36;
`;

const menuItemBase = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
`;

const MenuItem = styled.div`
  ${menuItemBase};
  color: #3a3f45;
  &:hover {
    background: ${BRAND};
    color: #fff;
  }
`;

const MenuItemAlert = styled.div`
  ${menuItemBase};
  background: ${BRAND};
  color: #fff;
  font-weight: 600;
  &:hover {
    filter: brightness(0.96);
  }
`;

/* --------------------- simple variant (title) ---------------------- */

const Title = styled.div`
  flex: 1;
  text-align: center;
  font-weight: 700;
  color: #242a30;

  ${pc} {
    font-size: 30px;
  }
  ${tabletL} {
    font-size: 26px;
  }
  ${tabletP} {
    font-size: 22px;
  }
  ${mobile} {
    font-size: 18px;
  }
`;

/* ---------------------------- content ------------------------------ */

const MainWrapper = styled.div`
  ${pc} {
    padding-top: ${H.pc}px;
  }
  ${tabletL} {
    padding-top: ${H.tabletL}px;
  }
  ${tabletP} {
    padding-top: ${H.tabletP}px;
  }
  ${mobile} {
    padding-top: ${H.mobile}px;
  }
`;

/* ================================================================== */

const HomeHeader = () => {
  const url = window.location.href;
  const navigate = useNavigate();

  const loginUser = JSON.parse(localStorage.getItem("loginUser") || "{}");
  const [openProfile, setOpenProfile] = useState<boolean>(false);
  const [openNav, setOpenNav] = useState<boolean>(false);
  const [openCalendar, setOpenCalendar] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(1);
  const [overPastList, setOverPastList] = useState<any[]>([]);

  const profileRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);

  /* 바깥 클릭 시 열린 메뉴 닫기 (Backdrop 대신 ref 기반) */
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        openProfile &&
        profileRef.current &&
        !profileRef.current.contains(t)
      ) {
        setOpenProfile(false);
      }
      if (openNav && navRef.current && !navRef.current.contains(t)) {
        setOpenNav(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openProfile, openNav]);

  useEffect(() => {
    const fetchAndFilterContracts = async () => {
      try {
        const response = await getContractList();
        if (response?.data?.receiptList) {
          setOverPastList(
            filterOverPastContractList(response.data.receiptList)
          );
        }
      } catch (error) {
        console.error("견적 리스트 조회 중 오류 발생:", error);
      }
    };
    fetchAndFilterContracts();
  }, []);

  useEffect(() => {
    if (url.includes("/contractlist")) setSelectedTab(2);
    else if (url.includes("/onsitecontract")) setSelectedTab(3);
    else if (url.includes("/enterpriseContractList")) setSelectedTab(4);
    else setSelectedTab(1);
  }, [url]);

  const closeAll = () => {
    setOpenProfile(false);
    setOpenNav(false);
  };

  const logout = () => {
    if (window.confirm("로그아웃 하시겠습니까?")) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("loginUser");
      navigate("/login");
    }
  };

  const handleOverPastAlert = () => {
    alert(`      상담 7일 경과 후 아직까지 미계약상태로
      남아있는 견적이 ${overPastList.length}건 있습니다.
      "계약" 혹은 "취소" 상태로 변경하세요.
      `);
    setOpenProfile(false);
  };

  const go = (path: string) => () => {
    navigate(path);
    closeAll();
  };
  const doRefresh = () => window.location.reload();
  const handleOpenCalendar = () => {
    setOpenCalendar(true);
    setOpenProfile(false);
  };

  /* nav tabs (기업이전은 권한 있을 때만) */
  const tabs = [
    { index: 1, label: "홈", path: "/", accent: BRAND },
    { index: 2, label: "견적리스트", path: "/contractlist", accent: BRAND },
    { index: 3, label: "현장접수", path: "/onsitecontract", accent: GREEN },
    ...(loginUser.isCompanyContractTeam
      ? [
          {
            index: 4,
            label: "기업이전",
            path: "/enterpriseContractList",
            accent: BRAND,
          },
        ]
      : []),
  ];

  /* 프로필 드롭다운 (공통) */
  const ProfileMenu = (
    <UserArea ref={profileRef}>
      <UserButton onClick={() => setOpenProfile((v) => !v)}>
        <IconBox>
          <ProfileIcon />
          {overPastList.length > 0 && <Badge>{overPastList.length}</Badge>}
        </IconBox>
        <UserName>{loginUser.name} 님</UserName>
      </UserButton>

      {openProfile && (
        <Dropdown>
          <NameLine>{loginUser.name} 님</NameLine>
          {overPastList.length > 0 && (
            <MenuItemAlert onClick={handleOverPastAlert}>
              미계약 {overPastList.length}건
            </MenuItemAlert>
          )}
          <MenuItem onClick={handleOpenCalendar}>손없는 날</MenuItem>
          <MenuItem onClick={doRefresh}>강력 새로고침</MenuItem>
          <MenuItem onClick={go("/profile")}>프로필</MenuItem>
          <MenuItem onClick={logout}>로그아웃</MenuItem>
        </Dropdown>
      )}
    </UserArea>
  );

  const isSimple =
    loginUser.userType === "mattress" || loginUser.userType === "plumber";

  /* ----------------- mattress / plumber (간단형) ----------------- */
  if (isSimple) {
    const title =
      loginUser.userType === "mattress" ? "매트리스케어" : "배관케어";
    return (
      <>
        {openCalendar && (
          <CalendarModal onClose={() => setOpenCalendar(false)} />
        )}
        <Header>
          <Logo onClick={go("/")}>
            <TonginLogoIcon />
          </Logo>
          <Title>{title}</Title>
          {ProfileMenu}
        </Header>
      </>
    );
  }

  /* ------------------------- 일반 사용자 ------------------------- */
  return (
    <>
      {openCalendar && <CalendarModal onClose={() => setOpenCalendar(false)} />}

      <Header>
        {/* 모바일: 좌측 햄버거 + nav 드로어 */}
        <NavMobile ref={navRef}>
          <Hamburger onClick={() => setOpenNav((v) => !v)}>
            <span />
            <span />
            <span />
          </Hamburger>
          {openNav && (
            <Drawer>
              {tabs.map((t) => (
                <DrawerLink
                  key={t.index}
                  $active={selectedTab === t.index}
                  $accent={t.accent}
                  onClick={go(t.path)}
                >
                  {t.label}
                </DrawerLink>
              ))}
            </Drawer>
          )}
        </NavMobile>

        {/* PC / 태블릿: 좌측 로고 */}
        <Logo onClick={go("/")}>
          <TonginLogoIcon />
        </Logo>

        {/* PC / 태블릿: 가운데 메뉴탭 */}
        <Nav>
          {tabs.map((t) => (
            <Tab
              key={t.index}
              $active={selectedTab === t.index}
              $accent={t.accent}
              onClick={go(t.path)}
            >
              {t.label}
            </Tab>
          ))}
        </Nav>

        {/* 우측 프로필 (공통) */}
        {ProfileMenu}
      </Header>
    </>
  );
};

export default function Layout() {
  return (
    <>
      <HomeHeader />
      <MainWrapper>
        <Outlet />
      </MainWrapper>
    </>
  );
}
